import { inject, injectable } from 'inversify'
import { computed, makeObservable } from 'mobx'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import { NotificationRepository, type NotificationType } from './notification.repository'

export type { NotificationType }

/**
 * What the component renders. A *copy* of the PM, not the PM itself: returning
 * `notificationListPm` handed the framework a live reference to Repository
 * state, which invariant 10 forbids for the ordinary reason — anything holding
 * it could mutate the Repository without going through an action, and MobX
 * would not even warn, because the write never touched an observable *field*.
 */
export type NotificationVm = {
  id: string
  type: NotificationType
  message: string
}

/**
 * Read side of the toast stack: what NotificationsComponent renders, plus the
 * dismiss action that component's close button fires.
 *
 * Code that *raises* a notification injects NotificationRepository instead —
 * it wants behaviour, not a ViewModel (draft §O).
 */
@injectable()
export class NotificationPresenter {
  @inject(NotificationRepository)
  private notificationRepository!: NotificationRepository

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  constructor() {
    makeObservable(this, {
      viewModel: computed,
    })
  }

  get viewModel(): { notifications: NotificationVm[]; showNotifications: boolean; dismissLabel: string } {
    const notifications = this.notificationRepository.notificationListPm

    return {
      // Display decision resolved here, not in the component (§3.4).
      showNotifications: notifications.length > 0,
      dismissLabel: this.i18nRepository.t('common.dismiss'),
      // Defined copy, property by property (§4.4 / invariant 10).
      notifications: notifications.map((notification) => ({
        id: notification.id,
        type: notification.type,
        message: notification.message,
      })),
    }
  }

  dismiss = (id: string) => {
    this.notificationRepository.dismiss(id)
  }
}
